import { CellProps } from 'react-table'
import { PlainHeader, sortableColumn, StackedCell, TableColumn } from 'app/components'
import { TFunction } from '_metronic/i18n/Metronici18n'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { HistoricoValorEmpresaList, ValorEmpresaList } from '../../core/_models'
import { EmpresaValoresActionCell } from './EmpresaValoresActionCell'

// Tipo e especialidade numa célula só (especialidade como apoio); os dois seguem ordenáveis.
const tipoEspecialidadeColumn = <T extends ValorEmpresaList | HistoricoValorEmpresaList>(
  t: TFunction
) =>
  sortableColumn<T>({
    id: 'tipo',
    useQueryRequest,
    fields: [
      { field: 'tipo', label: t('cols.tipo') },
      { field: 'especialidade.nome', label: t('cols.especialidade') },
    ],
    Cell: ({ row }: CellProps<T>) => (
      <StackedCell primary={row.original.tipo} secondary={row.original.especialidade?.nome} />
    ),
  })

const valorColumn = <T extends ValorEmpresaList | HistoricoValorEmpresaList>(t: TFunction) =>
  sortableColumn<T>({
    id: 'valor',
    cellWidth: '140px',
    useQueryRequest,
    fields: [{ field: 'valor', label: t('cols.valor') }],
    Cell: ({ row }: CellProps<T>) => <StackedCell tabular primary={row.original.valorFormatado} />,
  })

const valorRetornoColumn = <T extends ValorEmpresaList | HistoricoValorEmpresaList>(t: TFunction) =>
  sortableColumn<T>({
    id: 'valorRetorno',
    cellWidth: '140px',
    useQueryRequest,
    fields: [{ field: 'valorRetorno', label: t('cols.valor_retorno') }],
    Cell: ({ row }: CellProps<T>) => (
      <StackedCell tabular primary={row.original.valorRetornoFormatado} />
    ),
  })

export const empresaValoresColumns = (
  t: TFunction
): ReadonlyArray<TableColumn<ValorEmpresaList>> => [
  tipoEspecialidadeColumn<ValorEmpresaList>(t),
  sortableColumn<ValorEmpresaList>({
    id: 'faturamentoMinimo',
    cellWidth: '160px',
    hideOnMobile: true,
    useQueryRequest,
    fields: [{ field: 'faturamentoMinimo', label: t('cols.faturamento_minimo') }],
    Cell: ({ row }: CellProps<ValorEmpresaList>) => (
      <StackedCell tabular primary={row.original.faturamentoMinimo || undefined} />
    ),
  }),
  valorColumn<ValorEmpresaList>(t),
  valorRetornoColumn<ValorEmpresaList>(t),
  {
    Header: () => <PlainHeader>{t('cols.actions')}</PlainHeader>,
    id: 'actions',
    cellWidth: '132px',
    align: 'end',
    Cell: ({ row }: CellProps<ValorEmpresaList>) => (
      <EmpresaValoresActionCell valorEmpresa={row.original} />
    ),
  },
]

export const empresaValoresHistoricoColumns = (
  t: TFunction
): ReadonlyArray<TableColumn<HistoricoValorEmpresaList>> => [
  tipoEspecialidadeColumn<HistoricoValorEmpresaList>(t),
  valorColumn<HistoricoValorEmpresaList>(t),
  valorRetornoColumn<HistoricoValorEmpresaList>(t),
  // Vigência: início como principal, término como apoio; os dois seguem ordenáveis.
  sortableColumn<HistoricoValorEmpresaList>({
    id: 'inicio',
    cellWidth: '200px',
    useQueryRequest,
    fields: [
      { field: 'inicio', label: t('cols.inicio') },
      { field: 'termino', label: t('cols.termino') },
    ],
    Cell: ({ row }: CellProps<HistoricoValorEmpresaList>) => (
      <StackedCell
        tabular
        primary={row.original.inicioFormatado}
        secondary={
          row.original.terminoFormatado ? `até ${row.original.terminoFormatado}` : undefined
        }
      />
    ),
  }),
]
